import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import { namespace } from 'vuex-class';

import { FormattedUom, generateFormattedUom } from '@/common/uom';
import { Company } from '@/models/company.model';
import { Fuel } from '@/models/fuel.model';
import { Location } from '@/models/location.model';

import { RootState } from './index';


/**
 *
 * State
 *
 */
export type LocationState = Location;

const makeInitialState = (): LocationState => ({
  companyId: '',
  fuels: [],
  fullLocationId: '',
  heightUOM: '',
  locationContact: {
    address1: '',
    address2: '',
    city: '',
    contact: '',
    email: '',
    fax: '',
    phone: '',
    state: '',
    zip: '',
  },
  locationId: '',
  locationName: '',
  regionId: '',
  temperatureUOM: '',
  volumeUOM: '',
});

const state = makeInitialState;


/**
 *
 * Getters
 *
 */
interface LocationGetters {
  fuelById: { [key: string]: Fuel };
  heightUoms: FormattedUom;
  temperatureUoms: FormattedUom;
  volumeUoms: FormattedUom;
}

const getters: GetterTree<LocationState, RootState> = {
  defaultLocationId(state, getters, rootState) {
    let locationId = localStorage.getItem('locationId');

    if (!locationId) {
      const locations = rootState.company.locations;
      locationId = (locations && locations.length) ? locations[0].locationId : null;
    }

    return locationId;
  },

  fuelById(state) {
    return (state.fuels || []).reduce((result, fuel) => {
      result[fuel.fuelId] = fuel;
      return result;
    }, {});
  },

  heightUoms(state) {
    return generateFormattedUom(state.heightUOM);
  },

  temperatureUoms(state) {
    return generateFormattedUom(state.temperatureUOM);
  },

  volumeUoms(state) {
    return generateFormattedUom(state.volumeUOM);
  },
};


/**
 *
 * Mutations
 *
 */
const mutations: MutationTree<LocationState> = {
  reset(state) {
    const initialState = makeInitialState();
    for (const key in initialState) {
      if (initialState.hasOwnProperty(key)) {
        state[key] = initialState[key];
      }
    }
  },

  setValues(state, value: Location) {
    for (const key in value) {
      if (value.hasOwnProperty(key)) {
        state[key] = value[key];
      }
    }
  },

  setDefaultLocationId(state, value: string) {
    localStorage.setItem('locationId', value);
  },
};


/**
 *
 * Actions
 *
 */
interface LocationActions {
  loadDefault: () => void;
  setById: (id: string) => void;
}

const actions: ActionTree<LocationState, RootState> = {
  loadDefault(store) {
    const company: Company = store.rootState.company;
    const locationId = store.getters.defaultLocationId;
    const locations = company.locations || [];
    const isLocationValid = !!locations.find(m => m.locationId === locationId);

    if (isLocationValid) {
      store.dispatch('setById', locationId);
    }
    else if (locations.length) {
      store.dispatch('setById', company.locations[0].locationId);
    }
    else {
      console.warn('No locations loaded. Unable to load default location.');
    }
  },

  setById(store, id: string) {
    const company: Company = store.rootState.company;
    const location = company &&
                   company.locations &&
                   company.locations.find(m => m.locationId === id);

    if (location) {
      store.commit('setDefaultLocationId', location.locationId);
      store.commit('setValues', location);
    }
  },
};


export default {
  namespaced: true,
  actions,
  getters,
  mutations,
  state,
} as Module<LocationState, RootState>;


// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace LocationStore {
  export const Bind = namespace('location');
  export type Action <TProp extends keyof LocationActions> = LocationActions[TProp];
  export type Getter <TProp extends keyof LocationGetters> = LocationGetters[TProp];
  export type State <TProp extends keyof LocationState> = LocationState[TProp];
}
